import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useForm as useReactHookForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import useFormLiveSearch from '../live-search/useFormLiveSearch';
import {
  buildZodSchema,
  buildDefaultValues,
  buildFormLayout,
  sanitizeFormData,
  getFieldId,
  getFieldsetScrollName,
  getVisibleFieldIds,
  getDisabledFieldIds,
  isFieldReadOnly,
  isFieldDisabled,
  isRowVisible,
  DEFAULT_HIDE_CONDITIONS_STRATEGY,
} from './formUseCase';
import { encodeFieldName } from './fieldNameCodec';
import useComputedValues from './useComputedValues';

/**
 * @typedef {import('react-hook-form').FieldValues} FieldValues
 * @typedef useFormReturn
 * @property {import('react-hook-form').UseFormRegister<FieldValues>} register
 * @property {import('react-hook-form').Control<FieldValues, any>} control
 * @property {import('react-hook-form').UseFormWatch<FieldValues>} watch
 * @property {import('react-hook-form').UseFormSetValue<FieldValues>} setValue
 * @property {import('react-hook-form').UseFormGetValues<FieldValues>} getValues
 * @property {import('react-hook-form').UseFormReset<FieldValues>} reset
 * @property {import('react-hook-form').FormState<FieldValues>} formState
 * @property {(e?: import('react').BaseSyntheticEvent<object, any, any>) => Promise<void>} handleSubmit
 * @property {(fieldId: string) => any} getFieldError
 * @property {(fieldId: string) => any} getFieldValue
 * @property {import('react-hook-form').UseFormSetValue<FieldValues>} setFieldValue
 * @property {import('react-hook-form').UseFormReset<FieldValues>} resetForm
 * @property {import('zod').ZodSchema<FieldValues>} schema
 * @property {Record<string, any>} defaultValues
 * @property {FormLayout} layout
 * @property {boolean} hasMultipleTabs
 * @property {string[]} fieldsetIds
 * @property {import('react-hook-form').FieldErrors<FieldValues>} errors
 * @property {boolean} isValid
 * @property {boolean} isDirty
 * @property {boolean} isSubmitting
 * @property {number} activeTab
 * @property {(tab: number) => void} setActiveTab
 * @property {Record<string, Record<string, any>>} errorsByTabFieldset
 * @property {(field: FormItem) => boolean} isFieldVisible
 * @property {Set<string>} visibleFieldIds
 * @property {Set<string>} readOnlyFieldIds
 * @property {Set<string>} disabledFieldIds
 * @property {{
 *   hasLiveSearch: boolean,
 *   results: ApiLiveSearchResult[],
 *   isPending: boolean,
 *   isActionPending: boolean,
 *   queryTerms: string[],
 *   handleActionClick: (resultId: string, actionIndex: number) => void,
 * }} liveSearch
 * @property {string | null} sendFormUrlOverride - Live search editSendFormUrl override for next submit
 * @property {ApiFormSubmissionField[] | null} paramsOverride - Live search fill params override for next submit
 * @property {{ getCellValue: (rowId: string, colId: string) => string | undefined, isRowVisible: (row: FormTableRow) => boolean }} computed
 */

/**
 * Side panel OpenAPI form — schema, tabs, validation, optional live search.
 * @param {ApiFormResponse} formConfig - Form configuration object
 * @param {Function} [onSubmit] - Form submission handler (sanitizedData, submitUrl, submissionParams)
 * @param {object} [options] - Additional options for React Hook Form
 * @param {number} [defaultTab] - Default active tab
 * @param {boolean} [isLoading] - Form loading state
 * @param {(data: ApiFormPostResponse) => void} [onLiveSearchAutoSubmitSuccess]
 * @param {(error: Error) => void} [onLiveSearchAutoSubmitError]
 * @param {(error: unknown) => void} [onLiveSearchFillError]
 * @param {string} [containerId] - Scroll container id for namespaced fieldset scroll targets
 * @returns {useFormReturn} Form methods and computed values
 */
function useForm(
  formConfig,
  onSubmit,
  options = {},
  defaultTab = 0,
  isLoading = false,
  onLiveSearchAutoSubmitSuccess = undefined,
  onLiveSearchAutoSubmitError = undefined,
  onLiveSearchFillError = undefined,
  containerId = undefined,
) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [sendFormUrlOverride, setSendFormUrlOverride] = useState(/** @type {string | null} */ (null));
  const [paramsOverride, setParamsOverride] = useState(/** @type {ApiFormSubmissionField[] | null} */ (null));
  /** @type {Record<string, unknown>} */
  const restFormOptions = { ...options };
  delete restFormOptions.resolver;
  const defaultValues = useMemo(() => {
    if (isLoading || !formConfig) return {};
    return buildDefaultValues(formConfig);
  }, [formConfig, isLoading]);

  const schema = useMemo(() => {
    if (isLoading || !formConfig) return z.object({});
    return buildZodSchema(formConfig);
  }, [formConfig, isLoading]);

  const layout = useMemo(() => {
    if (isLoading || !formConfig) return { tabs: [], fieldsById: {} };
    return buildFormLayout(formConfig);
  }, [formConfig, isLoading]);

  const wrappedResolver = useMemo(() => {
    if (isLoading || !formConfig) {
      return zodResolver(schema);
    }
    const baseResolver = zodResolver(schema);
    /**
     * @param {FieldValues} values
     * @param {any} context
     * @param {any} resolverOptions
     * @returns {Promise<import('react-hook-form').ResolverResult<FieldValues>>}
     */
    return async (values, context, resolverOptions) => {
      const result = await baseResolver(values, context, resolverOptions);
      const visible = getVisibleFieldIds(
        formConfig,
        /** @type {Record<string, unknown>} */ (values || {}),
        DEFAULT_HIDE_CONDITIONS_STRATEGY,
      );
      const disabled = getDisabledFieldIds(
        formConfig,
        /** @type {Record<string, unknown>} */ (values || {}),
      );
      const fieldErrors = /** @type {Record<string, unknown>} */ (
        result.errors && typeof result.errors === 'object' ? result.errors : {}
      );
      Object.keys(fieldErrors).forEach((id) => {
        if (!visible.has(id) || disabled.has(id)) {
          delete fieldErrors[id];
        }
      });
      return result;
    };
  }, [schema, formConfig, isLoading]);

  const form = useReactHookForm({
    resolver: wrappedResolver,
    values: defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
    ...restFormOptions,
  });

  const watchedValues = form.watch();

  /** Stable string so visibleFieldIds Set identity only changes when visibility changes. */
  const visibleFieldIdsKey = useMemo(() => {
    if (isLoading || !formConfig) return '';
    const visible = getVisibleFieldIds(
      formConfig,
      /** @type {Record<string, unknown>} */ (watchedValues || {}),
      DEFAULT_HIDE_CONDITIONS_STRATEGY,
    );
    return [...visible].sort().join('\0');
  }, [formConfig, isLoading, watchedValues]);

  const visibleFieldIds = useMemo(() => {
    if (isLoading || !formConfig) return new Set();
    return getVisibleFieldIds(
      formConfig,
      /** @type {Record<string, unknown>} */ (watchedValues || {}),
      DEFAULT_HIDE_CONDITIONS_STRATEGY,
    );
  }, [formConfig, isLoading, visibleFieldIdsKey]);

  /** Stable key for readOnly/disabled field sets. */
  const conditionFieldIdsKey = useMemo(() => {
    if (isLoading || !formConfig) return '';
    const values = /** @type {Record<string, unknown>} */ (watchedValues || {});
    const { fieldsById } = layout;
    const readOnly = [];
    const disabled = [];
    Object.entries(fieldsById).forEach(([id, field]) => {
      if (isFieldReadOnly(field, values, fieldsById)) {
        readOnly.push(encodeFieldName(id));
      }
      if (isFieldDisabled(field, values, fieldsById)) {
        disabled.push(encodeFieldName(id));
      }
    });
    return `${readOnly.sort().join('\0')}|${disabled.sort().join('\0')}`;
  }, [formConfig, isLoading, watchedValues, layout.fieldsById]);

  const readOnlyFieldIds = useMemo(() => {
    if (isLoading || !formConfig) return new Set();
    const values = /** @type {Record<string, unknown>} */ (watchedValues || {});
    const { fieldsById } = layout;
    /** @type {Set<string>} */
    const readOnly = new Set();
    Object.entries(fieldsById).forEach(([id, field]) => {
      if (isFieldReadOnly(field, values, fieldsById)) {
        readOnly.add(encodeFieldName(id));
      }
    });
    return readOnly;
  }, [formConfig, isLoading, conditionFieldIdsKey, layout.fieldsById, watchedValues]);

  const disabledFieldIds = useMemo(() => {
    if (isLoading || !formConfig) return new Set();
    return getDisabledFieldIds(
      formConfig,
      /** @type {Record<string, unknown>} */ (watchedValues || {}),
    );
  }, [formConfig, isLoading, conditionFieldIdsKey, watchedValues]);

  /**
   * @param {FormItem} field
   * @returns {boolean}
   */
  const isFieldVisibleField = (field) => {
    const fieldId = getFieldId(field);
    return !!fieldId && visibleFieldIds.has(encodeFieldName(fieldId));
  };

  const isRowVisibleField = useCallback((row, formValues) => (
    isRowVisible(row, formValues, layout.fieldsById)
  ), [layout.fieldsById]);

  const computed = useComputedValues(
    formConfig,
    isLoading,
    form.watch,
    form.getValues,
    form.setValue,
    layout.fieldsById,
    isRowVisibleField,
  );

  useEffect(() => {
    setSendFormUrlOverride(null);
    setParamsOverride(null);
  }, [formConfig?.sendFormUrl]);

  const resolvedSendFormUrl = sendFormUrlOverride ?? formConfig?.sendFormUrl ?? '';
  const resolvedParams = paramsOverride ?? formConfig?.params ?? [];

  /**
   * Handle form submit
   * @param {import('react').BaseSyntheticEvent<object, any, any>} e - Form data
   * @returns {void}
   */
  const handleSubmit = useCallback(
    (e) => {
      if (!onSubmit) return undefined;
      return form.handleSubmit((data) => {
        if (!formConfig) return;

        const visible = getVisibleFieldIds(
          formConfig,
          /** @type {Record<string, unknown>} */ (data || {}),
          DEFAULT_HIDE_CONDITIONS_STRATEGY,
        );
        const visibleData = Object.fromEntries(
          Object.entries(data).filter(([id]) => visible.has(id)),
        );
        const sanitizedData = sanitizeFormData(visibleData, formConfig);
        onSubmit(sanitizedData, resolvedSendFormUrl, resolvedParams);
      })(e);
    },
    [form, formConfig, onSubmit, resolvedSendFormUrl, resolvedParams],
  );

  /**
   * Get field error
   * @param {string} fieldId - Field ID
   * @returns {import('react-hook-form').FieldError | import('react-hook-form').Merge<import('react-hook-form').FieldError, import('react-hook-form').FieldErrorsImpl<FieldValues>>} Field error
   */
  const getFieldError = (fieldId) => form.formState.errors[encodeFieldName(fieldId)];
  /**
   * Get field value
   * @param {string} fieldId - Field ID
   * @returns {FieldValues} Field value
   */
  const getFieldValue = (fieldId) => form.watch(encodeFieldName(fieldId));
  /**
   * Set field value
   * @param {string} fieldId - Field ID
   * @param {FieldValues} value - Field value
   * @returns {void}
   */
  const setFieldValue = (fieldId, value) => form.setValue(encodeFieldName(fieldId), value);

  /**
   * Reset form
   * @returns {void}
   */
  const resetForm = () => form.reset(defaultValues);

  /**
   * Get errors by tab and fieldset
   * @returns {Record<string, Record<string, any>>} Errors by tab and fieldset
   */
  const errorsByTabFieldset = layout.tabs.reduce((acc, tab) => {
    if (!acc[tab.id]) acc[tab.id] = {};

    tab.fieldsets.forEach((fieldset) => {
      fieldset.fields.forEach((field) => {
        const fieldId = getFieldId(field);
        const rhfName = fieldId ? encodeFieldName(fieldId) : null;
        if (
          fieldId
          && rhfName
          && visibleFieldIds.has(rhfName)
          && form.formState.errors[rhfName]
        ) {
          acc[tab.id][fieldId] = form.formState.errors[rhfName];
        }
      });
    });
    return acc;
  }, /** @type {Record<string, Record<string, any>>} */ ({}));

  const hasMultipleTabs = layout.tabs.length > 1;

  const fieldsetIds = useMemo(() => (layout.tabs?.length > 0
    ? layout.tabs.flatMap((tab) => tab.fieldsets.map((fieldset) => getFieldsetScrollName(containerId, fieldset.id)))
    : []), [layout.tabs, containerId]);

  const liveSearch = useFormLiveSearch({
    formConfig,
    layout,
    control: form.control,
    getValues: form.getValues,
    setValue: form.setValue,
    visibleFieldIdsKey,
    isLoading,
    onAutoSubmitSuccess: onLiveSearchAutoSubmitSuccess,
    onAutoSubmitError: onLiveSearchAutoSubmitError,
    onFillError: onLiveSearchFillError,
    onEditSendFormUrl: setSendFormUrlOverride,
    onEditParams: setParamsOverride,
  });

  return {
    // React Hook Form methods
    register: form.register,
    control: form.control,
    watch: form.watch,
    setValue: form.setValue,
    getValues: form.getValues,
    reset: form.reset,
    formState: form.formState,

    // Enhanced methods
    handleSubmit,
    getFieldError,
    getFieldValue,
    setFieldValue,
    resetForm,

    // Computed values
    schema,
    defaultValues,
    layout,
    hasMultipleTabs,
    fieldsetIds,

    // Form state helpers
    isValid: form.formState.isValid,
    isDirty: form.formState.isDirty,
    isSubmitting: form.formState.isSubmitting,
    errors: form.formState.errors,

    // Form Tab
    activeTab,
    setActiveTab,
    errorsByTabFieldset,

    isFieldVisible: isFieldVisibleField,
    visibleFieldIds,
    readOnlyFieldIds,
    disabledFieldIds,

    liveSearch,
    sendFormUrlOverride,
    paramsOverride,
    computed,
  };
}

export default useForm;
